import type { ListQueryConfig } from "#app/lib/hooks/use-list-query-state";

// One place that says how each entity list searches and sorts. These are
// CLIENT-ONLY decisions — which columns a search box matches, what the table
// starts sorted by — so they name schema FIELDS but describe no server shape.
// Pass one to `useListQueryState`; the resource registry pins one to each noun.
//
// Sort keys stay declared here because they cannot be introspected: every
// `orderBy` in the API is the `JSON` scalar, without exception
// (read the *WhereInput types in schema.gql).

/**
 * The columns issue search matches — THE app-wide contract. Every consumer
 * shares it so a widget's search can never disagree with the full-page list's.
 */
export const ISSUE_SEARCH_FIELDS = ["name", "description"] as const;

export const ISSUE_LIST: ListQueryConfig = {
  searchFields: ISSUE_SEARCH_FIELDS,
  defaultSortField: "creationDate",
  defaultSortDirection: "desc",
  initialSorting: [{ id: "creationDate", desc: true }],
  // `onlyCurrentUser` is a semantic flag on IssueWhereInput, not a column
  // predicate — the API reads it off the top level, never inside the AND.
  hoistedFilterKeys: ["onlyCurrentUser"],
};

export const TEAM_LIST: ListQueryConfig = {
  searchFields: ["teamName", "teamKey"],
  defaultSortField: "teamName",
};

export const MEMBER_LIST: ListQueryConfig = {
  searchFields: ["name", "email", "title"],
  defaultSortField: "name",
  // Core orders members by a computed display name that no single column
  // reproduces, so send no orderBy until the user picks one.
  deferOrderBy: true,
};

export const WORKFLOW_LIST: ListQueryConfig = {
  searchFields: ["name", "key"],
  defaultSortField: "name",
  initialSorting: [{ id: "name", desc: false }],
};

export const CUSTOM_FIELD_LIST: ListQueryConfig = {
  // CustomFieldWhereInput has NO boolean combinators, so an OR-fold over
  // columns is not expressible. It carries the server-side `search` flag
  // instead, which ILIKEs name/description/key in one predicate.
  searchFields: [],
  searchMode: "server",
  defaultSortField: "name",
};

export const ACTIVITY_LIST: ListQueryConfig = {
  // ActivityWhereInput has no `search` flag and no string column worth an
  // ILIKE — the feed filters by actor, subject and date, never by text.
  searchFields: [],
  defaultSortField: "creationDate",
  defaultSortDirection: "desc",
};

export const API_KEY_LIST: ListQueryConfig = {
  searchFields: [],
  searchMode: "server",
  defaultSortField: "creationDate",
  defaultSortDirection: "desc",
};

export const AUTOMATION_LIST: ListQueryConfig = {
  searchFields: [],
  searchMode: "server",
  defaultSortField: "lastUpdateDate",
  defaultSortDirection: "desc",
  // AutomationWhereInput takes no orderBy at all (read it in schema.gql). The
  // sort field is declared so the table can order what it holds; it is never
  // sent.
  deferOrderBy: true,
};

export const AUTOMATION_RUN_LIST: ListQueryConfig = {
  searchFields: [],
  defaultSortField: "creationDate",
  defaultSortDirection: "desc",
  deferOrderBy: true,
};

export const ISSUE_SOURCE_LIST: ListQueryConfig = {
  searchFields: [],
  searchMode: "server",
  defaultSortField: "name",
};

export const ISSUE_TYPE_LIST: ListQueryConfig = {
  searchFields: [],
  searchMode: "server",
  defaultSortField: "name",
};

export const NOTE_LIST: ListQueryConfig = {
  // NoteWhereInput filters by ids only — no text predicate exists.
  searchFields: [],
  defaultSortField: "creationDate",
  defaultSortDirection: "desc",
};

export const NOTIFICATION_LIST: ListQueryConfig = {
  // NotificationWhereInput's text predicate is `actionContains`, not `search`,
  // and the query takes no orderBy.
  searchFields: [],
  defaultSortField: "creationDate",
  defaultSortDirection: "desc",
  deferOrderBy: true,
};

export const REASON_LIST: ListQueryConfig = {
  searchFields: [],
  searchMode: "server",
  defaultSortField: "name",
};

export const ROLE_LIST: ListQueryConfig = {
  // `roles` takes neither where nor orderBy.
  searchFields: [],
  defaultSortField: "hierarchy",
  deferOrderBy: true,
};

export const STAGE_LIST: ListQueryConfig = {
  searchFields: [],
  searchMode: "server",
  defaultSortField: "sortOrder",
};

export const TRASH_LIST: ListQueryConfig = {
  searchFields: [],
  searchMode: "server",
  defaultSortField: "trashedAt",
  defaultSortDirection: "desc",
  // `trash` takes no orderBy.
  deferOrderBy: true,
};

export const WEBHOOK_LIST: ListQueryConfig = {
  // `webhooks` takes neither where nor orderBy.
  searchFields: [],
  defaultSortField: "creationDate",
  defaultSortDirection: "desc",
  deferOrderBy: true,
};

export const WIDGET_LIST: ListQueryConfig = {
  // `widgets(view:)` returns a plain array and takes no where/orderBy.
  searchFields: [],
  defaultSortField: "y",
  deferOrderBy: true,
};

export const WORKSPACE_LIST: ListQueryConfig = {
  // `workspaces` takes no arguments at all.
  searchFields: [],
  defaultSortField: "name",
  deferOrderBy: true,
};
